import { Box, Divider, Grid, Stack } from '@mui/material'
import { HearingSummaryProps } from 'components/HearingSummary'
import {
  formatInfraction,
  formatWelcomedOtherReasons,
  validateAddressList,
  validateBirthday,
  validateChoiceField,
  validateChoiceStudingField,
  validateDocumentList,
  validateFieldName,
  validateFieldSocialName,
  validateInfractionList,
  validatePhoneList,
  validateSocialNetworkList,
  validateStringNumber,
} from 'components/HearingSummary/utils/validate-field.util'
import Label from 'components/Label'
import React from 'react'
import { DocumentType } from 'types/document.type'
import {
  HearingIdentificationType,
  substances,
  TeenagerIdentificationType,
} from 'types/hearing-identification.type'
import { PhoneType } from 'types/phone.type'
import { SocialNetworkType } from 'types/social-network.type'
import { formatAddressField, formatAddressFieldPerson, formatPhoneNumber } from 'utils/text.util'
import { validateField } from 'utils/validate.util'
import { Field } from '../Field'

export const IdentificationGroup: React.FC<HearingSummaryProps<HearingIdentificationType>> = ({
  data,
}) => {
  const identificationSocialName = validateFieldSocialName(data)
  const identificationName = validateFieldName(data)
  const birthdayField = validateBirthday(data)
  const documentsField = validateDocumentList(data)
  const phonesField = validatePhoneList(data)
  const socialNetworksField = validateSocialNetworkList(data)
  const infractionsField = validateInfractionList(data)
  const hasWecomedInRJ =
    data &&
    data.identificacaoAdolescente &&
    data.identificacaoAdolescente.acolhidoInsticionalmenteEstadoRJ === 'S'

  const addressesField = validateAddressList(data)
  const hasAddress = addressesField.length > 0
  const mainAddress = hasAddress
    ? addressesField.find((item) => item.enderecoPrincipal === 'S')
    : null
  const othersAddress = hasAddress
    ? addressesField.filter((item) => item.enderecoPrincipal === 'N')
    : null

  const formatNaturalnessField = (): string => {
    if (!data) {
      return ''
    }

    const { descricaoIdMunicipioNaturalidade, descricaoIdEstadoNaturalidade } = data

    if (descricaoIdMunicipioNaturalidade && descricaoIdEstadoNaturalidade) {
      return `${descricaoIdMunicipioNaturalidade}/${descricaoIdEstadoNaturalidade}`
    } else if (descricaoIdEstadoNaturalidade) {
      return descricaoIdEstadoNaturalidade
    }

    return ''
  }

  const formatSalaryActivity = (item: TeenagerIdentificationType): string => {
    if (!item) {
      return ''
    }

    const { exerceAtividadeRemunerada, descricaoAtividadeRemunerada } = item

    if (exerceAtividadeRemunerada === 'S') {
      if (item.descricaoAtividadeRemunerada !== '') {
        return descricaoAtividadeRemunerada ? `Sim - ${descricaoAtividadeRemunerada}` : 'Sim'
      } else {
        return 'Sim'
      }
    } else if (exerceAtividadeRemunerada === 'N') {
      return 'Não'
    }

    return ''
  }

  const formatSubstances = (item: TeenagerIdentificationType): string => {
    if (!item || !item.usaSubstancia) {
      return ''
    }

    const useSubstances = item.usaSubstancia === 'S'

    if (useSubstances) {
      const substancesText = substances
        .filter((substance) => item[substance.name] === 'S')
        .map((substance) => substance.label)
        .join(', ')

      return substancesText ? `Sim - ${substancesText}.` : 'Sim'
    } else {
      return 'Não'
    }
  }

  const formatContact = (value?: string | null): string => {
    if (!value) {
      return ''
    }
    if (value === 'S') {
      return 'Sim'
    } else if (value === 'N') {
      return 'Não'
    } else if (value === 'F') {
      return 'Falecido'
    }
    return 'Eventualmente'
  }

  const formatDocumentField = (document: DocumentType): string => {
    const documentDescriptionString = validateField(document.descricaoIdTipoDocumento)
    const documentNumberString = validateField(document.numeroDocumento)
    if (documentDescriptionString !== '' && documentNumberString !== '') {
      return `${documentDescriptionString} - ${documentNumberString}`
    }
    return ''
  }

  const formatPhoneField = (phone: PhoneType): string => {
    const phoneString = validateField(phone.telefone)
    const descriptionString = validateField(phone.descricao)

    if (phoneString !== '' && descriptionString !== '') {
      return `${formatPhoneNumber(phoneString)} - ${descriptionString}`
    } else if (phoneString !== '') {
      return formatPhoneNumber(phoneString)
    }

    return ''
  }

  const formatSocialNetworkField = (socialNetwork: SocialNetworkType): string => {
    const socialNetWorkString = validateField(socialNetwork.redeSocial)
    const descriptionString = validateField(socialNetwork.descricao)

    if (socialNetWorkString !== '' && descriptionString !== '') {
      return `${socialNetWorkString} - ${descriptionString}`
    } else if (socialNetWorkString !== '') {
      return socialNetWorkString
    }

    return ''
  }

  const validateChildrenGuard = (item?: TeenagerIdentificationType): string => {
    if (!item) {
      return ''
    }
    const possuiFilho = data.identificacaoAdolescente.possuiFilho === 'S'
    return possuiFilho ? validateField(data.identificacaoAdolescente.descricaoGuardaFilho) : ''
  }

  const formatLivingWithField = (item?: TeenagerIdentificationType): string => {
    if (!item) {
      return ''
    }

    const isOtherOption =
      item.descricaoIdResidenciaResponsavel &&
      item.descricaoIdResidenciaResponsavel.toLowerCase().includes('outro')
    const otherOption = validateField(item.descricaoIdResidenciaResponsavel)
    const otherName = validateField(item.nomeResponsavelResidenciaOutro)

    return isOtherOption ? otherName : otherOption
  }

  const formatKeepInTouchWithMotherField = (item?: TeenagerIdentificationType): string => {
    if (!item) {
      return ''
    }

    const isKeepingTouch = item.mantemContatoMae === 'N'
    const description = formatContact(item.mantemContatoMae)
    const reason = validateField(item.motivoNaoMantemContatoMae)

    return isKeepingTouch ? `${description} - ${reason}` : description
  }

  const formatKeepInTouchWithFatherField = (item?: TeenagerIdentificationType): string => {
    if (!item) {
      return ''
    }

    const isKeepingTouch = item.mantemContatoPai === 'N'
    const description = formatContact(item.mantemContatoPai)
    const reason = validateField(item.motivoNaoMantemContatoPai)

    return isKeepingTouch ? `${description} - ${reason}` : description
  }

  return (
    <>
      <Label text='IDENTIFICAÇÃO DO ADOLESCENTE' v='h6' bold />
      <Divider />
      {identificationName && (
        <Grid container spacing={1} sx={{ mt: 1 }}>
          <Field label='Nome social:' text={identificationSocialName} fontWeight={400} />
          <Field label='Nome:' text={identificationName} fontWeight={400} />
          <Field label='Data de nascimento:' text={birthdayField} fontWeight={400} />
          <Field
            label='Idade:'
            text={validateStringNumber(String(data.identificacaoAdolescente.idade))}
            fontWeight={400}
          />
          <Field label='Gênero:' text={validateField(data.descricaoIdGenero)} fontWeight={400} />
          <Field label='Cor: ' text={validateField(data.descricaoIdRaca)} fontWeight={400} />
          {documentsField && documentsField.length > 0 && (
            <Grid item xs={12}>
              <Stack direction='column' spacing={1}>
                <Label text='Documentos: ' sx={{ fontSize: 14 }} bold />
                {documentsField.map((item, i) => (
                  <p key={i} style={{ fontSize: 14 }}>
                    {formatDocumentField(item)}
                  </p>
                ))}
              </Stack>
            </Grid>
          )}
          {hasAddress && (
            <>
              {mainAddress && (
                <Grid item xs={12}>
                  <Label text='Endereço principal:' sx={{ fontSize: 14 }} bold />
                  <span style={{ fontSize: 14 }}>{formatAddressFieldPerson(mainAddress)}</span>
                </Grid>
              )}
              {othersAddress && (
                <Grid item xs={12}>
                  <Label text='Demais endereços:' sx={{ fontSize: 14 }} bold />
                  <Box display='flex' flexDirection='column'>
                    {othersAddress.map((item) => (
                      <span key={item.id} style={{ fontSize: 14 }}>
                        {formatAddressFieldPerson(item)}
                      </span>
                    ))}
                  </Box>
                </Grid>
              )}
            </>
          )}
          {phonesField && phonesField.length > 0 && (
            <Grid item xs={12}>
              <Stack direction='column' spacing={1}>
                <Label text='Telefones: ' sx={{ fontSize: 14 }} bold />
                {phonesField.map((item, i) => (
                  <p key={i} style={{ fontSize: 14 }}>
                    {formatPhoneField(item)}
                  </p>
                ))}
              </Stack>
            </Grid>
          )}
          {socialNetworksField && socialNetworksField.length > 0 && (
            <Grid item xs={12}>
              <Stack direction='column' spacing={1}>
                <Label text='Redes sociais: ' sx={{ fontSize: 14 }} bold />
                {socialNetworksField.map((item, i) => (
                  <p key={i} style={{ fontSize: 14 }}>
                    {formatSocialNetworkField(item)}
                  </p>
                ))}
              </Stack>
            </Grid>
          )}
          <Field
            label='Email do adolescente/representante legal:'
            text={validateField(data.email)}
            fontWeight={400}
          />
          <Field label='Nome da mãe:' text={validateField(data.nomeMae)} fontWeight={400} />
          <Field label='Nome do pai:' text={validateField(data.nomePai)} fontWeight={400} />
          <Field
            label='Naturalidade (Cidade/UF):'
            text={formatNaturalnessField()}
            fontWeight={400}
          />
          <Field
            label='Grau de escolaridade:'
            text={validateField(data.descricaoIdGrauEscolaridade)}
            fontWeight={400}
          />
          <Field
            label='Instituição de ensino:'
            text={validateStringNumber(data.identificacaoAdolescente.nomeInstituicaoEnsino)}
            fontWeight={400}
          />
          <Field
            label='Está estudando:'
            text={validateChoiceStudingField(data.identificacaoAdolescente.estaEstudando)}
            fontWeight={400}
          />
          {data.identificacaoAdolescente.estaEstudando === 'E' && (
            <>
              <Field
                label='Tempo da evasão:'
                text={validateStringNumber(data.identificacaoAdolescente.descricaoTempoEvasao)}
                fontWeight={400}
              />
              <Field
                label='Motivo da evasão:'
                text={validateStringNumber(data.identificacaoAdolescente.descricaoMotivoEvasao)}
                fontWeight={400}
              />
              <Field
                label='Foi procurado pela escola:'
                text={validateChoiceField(data.identificacaoAdolescente.procuradoEscola)}
                fontWeight={400}
              />
              <Field
                label='Foi procurado pelo Conselho Tutelar:'
                text={validateChoiceField(data.identificacaoAdolescente.procuradoConselhoTutelar)}
                fontWeight={400}
              />
            </>
          )}
          {data.identificacaoAdolescente.estaEstudando === 'C' && (
            <Field
              label='Ano de conclusão:'
              text={validateStringNumber(data.identificacaoAdolescente.anoConclusaoEstudo)}
              fontWeight={400}
            />
          )}
          <Field
            label='Atividade remunerada:'
            text={formatSalaryActivity(data.identificacaoAdolescente)}
            fontWeight={400}
          />
          <Field
            label='Reside com quem:'
            text={formatLivingWithField(data.identificacaoAdolescente)}
            fontWeight={400}
          />
          <Field
            label='Adolescente mantém contato com o pai:'
            text={formatKeepInTouchWithFatherField(data.identificacaoAdolescente)}
            fontWeight={400}
          />
          <Field
            label='Adolescente mantém contato com a mãe:'
            text={formatKeepInTouchWithMotherField(data.identificacaoAdolescente)}
            fontWeight={400}
          />
          <Field
            label='Adolescente recebe pensão alimentícia ou outro auxílio material do representante legal:'
            text={formatContact(data.identificacaoAdolescente.recebePensaoAlimenticia)}
            fontWeight={400}
          />
          <Field
            label='Tem filhos:'
            text={validateChoiceField(data.identificacaoAdolescente.possuiFilho)}
            fontWeight={400}
          />
          <Field
            label='Quantidade de filhos:'
            text={
              data.identificacaoAdolescente.possuiFilho === 'S'
                ? `${data.identificacaoAdolescente.quantidadeFilho}`
                : ''
            }
            fontWeight={400}
          />
          <Field
            label='Guarda dos filhos:'
            text={validateChildrenGuard(data.identificacaoAdolescente)}
            fontWeight={400}
          />
          <Field
            label='Em gestação:'
            text={validateChoiceField(data.identificacaoAdolescente.estaGestante)}
            fontWeight={400}
          />
          <Field
            label='Tem irmãos:'
            text={validateChoiceField(data.identificacaoAdolescente.possuiIrmao)}
            fontWeight={400}
          />
          <Field
            label='Quantidade de Irmãos:'
            text={
              data.identificacaoAdolescente.possuiIrmao === 'S'
                ? `${data.identificacaoAdolescente.quantidadeIrmao}`
                : ''
            }
            fontWeight={400}
          />
          <Field
            label='Os responsáveis trabalham:'
            text={validateChoiceField(data.identificacaoAdolescente.responsavelTrabalha)}
            fontWeight={400}
          />
          {data.identificacaoAdolescente.responsavelTrabalha === 'S' && (
            <Field
              label='Renda familiar:'
              text={validateStringNumber(data.identificacaoAdolescente.descricaoIdRendaFamiliar)}
              fontWeight={400}
            />
          )}
          <Field
            label='Usa substancias:'
            text={formatSubstances(data.identificacaoAdolescente)}
            fontWeight={400}
          />
          <Field
            label='Faz algum tratamento de saúde mental:'
            text={validateChoiceField(data.identificacaoAdolescente.tratamentoMental)}
            fontWeight={400}
          />
          <Field
            label='Tratamento de saúde mental:'
            text={validateStringNumber(data.identificacaoAdolescente.tipoTratamentoMental)}
            fontWeight={400}
          />
          <Field
            label='Instituição de tratamento de saúde mental:'
            text={validateStringNumber(data.identificacaoAdolescente.instituicaoTratamentoMental)}
            fontWeight={400}
          />
          <Field
            label='Está ou já esteve em situação de rua:'
            text={validateChoiceField(data.identificacaoAdolescente.situacaoRua)}
            fontWeight={400}
          />
          <Field
            label='Já foi acolhido institucionalmente:'
            text={validateChoiceField(data.identificacaoAdolescente.acolhidoInsticionalmente)}
            fontWeight={400}
          />
          <Field
            label='Estado do Rio de Janeiro:'
            text={validateChoiceField(
              data.identificacaoAdolescente.acolhidoInsticionalmenteEstadoRJ,
            )}
            fontWeight={400}
          />
          {!hasWecomedInRJ && (
            <Field
              label='Estado:'
              text={validateStringNumber(
                data.identificacaoAdolescente.descricaoIdAcolhidoInsticionalmenteOutroEstado,
              )}
              fontWeight={400}
            />
          )}
          {hasWecomedInRJ && (
            <>
              <Field
                label='Município:'
                text={validateStringNumber(
                  data.identificacaoAdolescente
                    .descricaoIdAcolhidoInsticionalmenteMunicipioEstadoRJ,
                )}
                fontWeight={400}
              />
              <Field
                label='Instituição:'
                text={validateStringNumber(
                  data.identificacaoAdolescente
                    .descricaoIdAcolhidoInsticionalmenteServicoAcolhimento,
                )}
                fontWeight={400}
              />
            </>
          )}

          <Field
            label='Qual o motivo:'
            text={formatWelcomedOtherReasons(data.identificacaoAdolescente)}
            fontWeight={400}
          />
          <Grid item xs={12}>
            <Stack direction='column' spacing={1}>
              <Field
                label='Antecedentes infracionais:'
                text={validateChoiceField(data.identificacaoAdolescente.antecedenteInfracional)}
                fontWeight={400}
              />
              {infractionsField.map((item, i) => (
                <p key={i} style={{ fontSize: 14 }}>
                  {formatInfraction(item, i)}
                </p>
              ))}
            </Stack>
          </Grid>
        </Grid>
      )}
    </>
  )
}
